import { Button, Stack, SxProps, Theme, Typography } from '@mui/material'
import { Icons } from 'utoniq-ui'

import { SalesStatus } from '@/clients/utoniq-core/schema'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'

export type ConsoleSupplementLinkAreaProps = {
  itemId: string
  loading: boolean
  isPublished: boolean
  saleStatus: string
  onToggleSelling: (id: string, isActive: boolean) => Promise<void> | void
  onToggleActive: (id: string, isActive: boolean) => Promise<void> | void
  onRemove: () => Promise<void> | void
  sx?: SxProps<Theme>
}

export const ConsoleSupplementLinkArea: Organism<ConsoleSupplementLinkAreaProps> =
  ({
    itemId,
    loading,
    isPublished,
    saleStatus,
    onToggleSelling,
    onToggleActive,
    onRemove,
    sx,
  }) => {
    const { t } = useTranslation('consoleItemDetail')
    const { routerPages, generatePath } = useRouter()

    const supplementLinkLeft = [
      {
        title: 'itemInfo',
        icon: (
          <Icons.ItemInfo
            fontSize="small"
            sx={{ mx: 'auto', color: 'grey.600' }}
          />
        ),
        href: !loading
          ? generatePath(routerPages.ConsoleItemInformation, {
              itemId: itemId ?? '#',
            })
          : undefined,
      },
    ]
    const supplementLinkRight = [
      {
        title: 'sale',
        icon:
          saleStatus === SalesStatus.Active ? (
            <Icons.CheckCircle
              fontSize="small"
              sx={{ mx: 'auto', color: 'grey.600' }}
            />
          ) : (
            <Icons.Unpublished
              fontSize="small"
              sx={{ mx: 'auto', color: 'grey.600' }}
            />
          ),
        onClick: () =>
          onToggleSelling(itemId, saleStatus === SalesStatus.Active),
      },
      {
        title: 'release',
        icon: isPublished ? (
          <Icons.Display
            fontSize="small"
            sx={{ mx: 'auto', color: 'grey.600' }}
          />
        ) : (
          <Icons.Hide fontSize="small" sx={{ mx: 'auto', color: 'grey.600' }} />
        ),
        onClick: () => onToggleActive(itemId, isPublished),
      },
      {
        title: 'edit',
        icon: (
          <Icons.Pencil
            fontSize="small"
            sx={{ mx: 'auto', color: 'grey.600' }}
          />
        ),
        href: !loading
          ? generatePath(routerPages.ConsoleItemEdit, { itemId })
          : undefined,
      },
      {
        title: 'delete',
        icon: (
          <Icons.Trash
            fontSize="small"
            sx={{ mx: 'auto', color: 'grey.600' }}
          />
        ),
        onClick: onRemove,
      },
    ]

    return (
      <CellFrame>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            py: 2,
            px: 4,
            minHeight: 64,
            ...sx,
          }}
        >
          {[supplementLinkLeft, supplementLinkRight].map(
            (supplementLink, index) => (
              <Stack direction="row" alignItems="center" key={index}>
                {supplementLink.map(({ title, icon, ...props }, subIndex) => {
                  if (title === 'sale' && loading) return null
                  if (title === 'release' && loading) return null
                  return (
                    <Button
                      variant="text"
                      key={subIndex}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        p: 1,
                        borderRadius: 0,
                        textAlign: 'center',
                        minWidth: 44,
                        '&.Mui-disabled': {
                          background: 'inherit',
                          color: 'text.disabled',
                          '.MuiSvgIcon-root': {
                            color: 'inherit',
                          },
                          '.MuiTypography-root': {
                            color: 'text.disabled',
                          },
                        },
                      }}
                      disabled={loading}
                      {...props}
                    >
                      {icon}
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ pt: 1 }}
                      >
                        {t((title ?? '') as string)}
                      </Typography>
                    </Button>
                  )
                })}
              </Stack>
            )
          )}
        </Stack>
      </CellFrame>
    )
  }
